import type { Providers, ResultsState, ValidationStatuses } from 'enums'
import { InferType, array, object, string } from 'yup'

import { type FileContent } from 'services/types'

import { DataAccessRequestParticipantFileStatus, ResultRequestType, UploadedFileProvider } from './enums'

export type FileChangeStatus = 'unchanged' | 'changed'
export { ResultRequestType } from './enums'

export interface ReRequest {
  fileChangeStatus?: FileChangeStatus
  originalApprovalDate: string
  originalApprovalMethod: 'automated' | 'manualApproval' | 'manualApprovalAfterAutoCheckFailed'
  originallyApprovedBy: string
  rootRequestId: string
}

interface ResultFormSubmissionOption {
  id: string
  label: string
}

interface ResultFormSubmissionUploadedFileBase {
  provider: UploadedFileProvider
  originalFileName: string
  mimeType: string
  sizeInBytes: number
  prefix: string
}

interface ResultFormSubmissionUploadedAwsFile extends ResultFormSubmissionUploadedFileBase {
  provider: UploadedFileProvider.Aws
  bucket: string
}

interface ResultFormSubmissionUploadedAzureFile extends ResultFormSubmissionUploadedFileBase {
  provider: UploadedFileProvider.Azure
  blobStorageAccountName: string
  blobContainerName: string
}

type ResultFormSubmissionFile = ResultFormSubmissionUploadedAwsFile | ResultFormSubmissionUploadedAzureFile

interface ResultFormSubmissionItem {
  fieldId: string
  type: string
  label: string
  value?: unknown
  values?: unknown[]
  helpText?: string
  required?: boolean
  allowMultiple?: boolean
  content?: string
  options?: ResultFormSubmissionOption[]
  uploadedFiles?: ResultFormSubmissionFile[]
}

interface ResultFormSubmissionBlock {
  id: string
  kind: string
  items: ResultFormSubmissionItem[]
  title?: string
  item?: ResultFormSubmissionItem
}

export interface ResultFormSubmission {
  formId: string
  type: string
  formName: string
  logoUrl?: string
  kind: string
  submittedAt: string
  blocks: ResultFormSubmissionBlock[]
}

interface AppealReviewPreviousRequest {
  _id: string
  status: ResultsState
  verifiedBy: ResultUser & { clients?: string[] }
  verifiedAt: string
  rejectionReason?: string
  beforeCheckLocation: ResultLocation & {
    hash?: string
    lastModified?: string
  }
  formSubmission?: ResultFormSubmission
}

export interface AppealReview {
  parentRequestId: string
  previousRequest: AppealReviewPreviousRequest
}

interface ResultUserIdentity {
  email: string
  _id: string
  // Optional: airlock-be does not yet populate a name for result assignees; the UI falls back to email.
  name?: string
}

export type ResultUser = ResultUserIdentity

interface ResultLocation {
  bucket?: string
  storageAccountName?: string
  containerName?: string
  prefix: string
  isFile: boolean
  provider: Providers
  lastModified?: string
}

interface ExportRequestSubmissionStatus {
  eligible: boolean
  submissionRequestId?: string
  submittedAt?: string
}

interface ExportRequestAttestationReviewPreviousRequest {
  _id: string
  status: ResultsState
  verifiedBy?: ResultUser
  verifiedAt?: string
  rejectionReason?: string
  beforeCheckLocation: ResultLocation
  formSubmission?: ResultFormSubmission
}

interface ExportRequestAttestationReview {
  parentRequestId: string
  previousRequest?: ExportRequestAttestationReviewPreviousRequest
  previousRequests?: ExportRequestAttestationReviewPreviousRequest[]
}

interface ExportRequestStorageItem {
  isDir: boolean
  lastModified: string
  name: string
  path?: string
  size?: number
}

export interface DataAccessRequestPhenotypicQuery {
  participantsAppliedType?: string
  query?: Record<string, unknown>
  queryView?: Record<string, unknown>[]
  phenotypeFilters?: Record<string, unknown>[]
  sqlQuery?: string
  naturalLanguageQuery?: string
}

export interface DataAccessRequestGenotypicQuery {
  filterJobId?: string
  filterJobName?: string
  criteria?: Record<string, unknown>
  queryMarkers?: string
  genotypes?: string[]
}

export interface DataAccessRequestQuery {
  phenotypic?: DataAccessRequestPhenotypicQuery
  genotypic?: DataAccessRequestGenotypicQuery
}

export interface DataAccessRequestCohort {
  federatedCohortId: string
  sourceCohortId: string
  numberOfParticipants: number
  totalNumberOfParticipants: number
}

export interface DataAccessRequestParticipantFile {
  status: DataAccessRequestParticipantFileStatus
  displayName?: string
}

export interface DataAccessRequestQuerySource {
  query?: DataAccessRequestQuery
  cohort?: DataAccessRequestCohort
  hasParticipantRestrictions?: boolean
}

export interface ExportRequestDataAccessRequest extends DataAccessRequestQuerySource {
  datasetName: string
  datasourceId: string
  federatedConnectionId?: string
  sourceOrganisationId: string
  sourceOrganisationName?: string
  sourceWorkspaceId: string
  sourceWorkspaceName?: string
  requesterOrganisationId?: string
  requesterOrganisationName?: string
  requesterWorkspaceId?: string
  requesterWorkspaceName?: string
  datasetColor?: string
  biobankId?: string
  omopSchemaName?: string
  participantFile?: DataAccessRequestParticipantFile
}

export interface ResultLabel {
  _id: string
  name: string
}

type AutomatedChecksMode = 'ran' | 'skipped'

interface AutomatedCheckJobView {
  jobId: string
  status: AutoCheckJobStatuses
  workflowName?: string
  workflowVersion?: string
}

// The optional fields are dependent on the job being launched
interface AutomatedChecksView {
  mode: AutomatedChecksMode
  triggeredAt?: string
  jobs?: AutomatedCheckJobView[]
}

export enum AutoCheckJobStatuses {
  Pending = 'pending',
  Passed = 'passed',
  Failed = 'failed'
}

export interface Result {
  _id: string
  requestingUser: ResultUser
  description: string
  beforeCheckLocation: ResultLocation
  afterCheckLocation: ResultLocation
  verifiedBy: ResultUser
  status: ResultsState
  verifiedAt: string
  createdAt: string
  requestType: ResultRequestType
  startTime?: string
  endTime?: string
  failureReason?: string
  cost?: CostInfo
  subscription?: Subscription
  workspaceId?: string
  workspaceName?: string
  resourceTypeId?: string
  resourceTypeName?: string
  isAutomaticApproved?: boolean
  rejectionReason?: string
  contentDescription?: string
  exportReason?: string
  downloadLinkExpiresInSeconds?: number
  name?: string
  validationStatus?: ValidationStatuses
  assignedTo?: ResultUser
  assignedAt?: string
  assignedBy?: ResultUser
  parentRequestId?: string
  rootRequestId?: string
  reRequest?: ReRequest
  fileChangeStatus?: FileChangeStatus
  reviewSubStatus?: string
  appealReview?: AppealReview
  formSubmission?: ResultFormSubmission
  /**
   * Requester-side appeal submission state (`buildAppealState` on airlock-be).
   * `eligible: true` means the export request is already rejected, not awaiting attestation,
   * and the requester may submit an appeal form.
   */
  appeal?: ExportRequestSubmissionStatus
  /**
   * Requester-side attestation submission state (`buildAttestationState` on airlock-be).
   * `eligible: true` means the export request is already rejected with
   * `reviewSubStatus: attestation-required` and the requester may submit the attestation form.
   * It does not indicate that an admin can request attestation from pending verification.
   */
  attestation?: ExportRequestSubmissionStatus
  dataAccessRequest?: ExportRequestDataAccessRequest
  attestationReview?: ExportRequestAttestationReview
  storageContents?: ExportRequestStorageItem[]
  labels?: ResultLabel[]
  automatedChecks?: AutomatedChecksView
}

export interface UpdateApproveResultParams {
  downloadLinkExpiresInSeconds?: number
}

export interface GetFileContentArgs {
  runId: string
  bucket?: string
  fileKey: string
  containerName?: string
  provider?: UploadedFileProvider
  storageAccountName?: string
  fetchSignedUrlOnly?: boolean
}

export type GetTextFileContentArgs = GetFileContentArgs
export type GetBinaryFileContentArgs = GetFileContentArgs

interface FileContentResponse<TFileContent> {
  signedUrl: string
  fileKey: string
  fileContent: TFileContent
}

export type GetFileContentResponse = FileContentResponse<FileContent>
export type GetTextFileContentResponse = FileContentResponse<FileContent>
export type GetBinaryFileContentResponse = FileContentResponse<ArrayBuffer>

export interface GetSignedUrlForApprovedResultParams {
  resultId: string
  token: string | null
}

export interface GetSignedUrlForApprovedResultResponse {
  signedUrl: string
  fileKey: string
  expiresAt?: string
}

export interface GetVerificationAttachmentSignedUrlParams {
  resultId: string
  fieldId: string
  attachmentIndex: number
}

export interface GetVerificationAttachmentSignedUrlResponse {
  signedUrl: string
  fileKey?: string
}

export interface GetParticipantFileSignedUrlParams {
  resultId: string
}

export interface GetParticipantFileSignedUrlResponse {
  signedUrl: string
  fileName: string
}

export const getAutomatedChecksReportResponseSchema = object({
  fileKey: string().required(),
  signedUrl: string().required(),
  expiresAt: string().required()
}).required()

export type GetAutomatedChecksReportResponse = InferType<typeof getAutomatedChecksReportResponseSchema>

export interface ResultsInfo {
  data: Result[]
  pagination: PaginationMeta
}

// GetWorkspacesFromExportRequests
export type GetWorkspacesFromExportRequestsResponseItem = {
  _id: string
  name: string
}

export type GetWorkspacesFromExportRequestsResponse = {
  data: GetWorkspacesFromExportRequestsResponseItem[]
}

const getLabelsFromExportRequestsResponseItemSchema = object({
  _id: string().required(),
  name: string().required(),
  procurementId: string().required()
}).required()

export type GetLabelsFromExportRequestsResponseItem = InferType<typeof getLabelsFromExportRequestsResponseItemSchema>

type GetRequestersFromExportRequestsResponseItem = ResultUserIdentity

export type GetRequestersFromExportRequestsResponse = {
  data: GetRequestersFromExportRequestsResponseItem[]
}

export interface BulkAssignExportRequestsParams {
  resultIds: string[]
  assigneeId: string
  /**
   * Whether the acting manager confirms they reviewed the request before assigning it for peer
   * review. Optional at the API boundary (defaults to false); the portal always sends it.
   */
  reviewConfirmed?: boolean
}

export interface BulkAssignExportRequestsResponse {
  successCount: number
  failedIds: string[]
}

export interface GetResultsVerificationAssigneesResponse {
  data: GetResultsVerificationAssigneesResponseItem[]
}

export type GetResultsVerificationAssigneesResponseItem = ResultUserIdentity

export interface GetAssignableAssigneesResponse {
  data: GetAssignableAssigneesResponseItem[]
}

export type GetAssignableAssigneesResponseItem = ResultUserIdentity

// Export request labels schemas
const exportRequestLabelSchema = object({
  _id: string().required(),
  name: string().required(),
  procurementId: string().required()
}).required()

export const getLabelsFromExportRequestsResponseSchema = object({
  data: array(exportRequestLabelSchema).required()
}).required()

export type GetLabelsFromExportRequestsResponse = InferType<typeof getLabelsFromExportRequestsResponseSchema>

export interface BulkUnassignExportRequestsParams {
  resultIds: string[]
}

export interface BulkUnassignExportRequestsResponse {
  description: 'Unassign export requests'
}

export interface CreateResultLabelParams {
  name: string
}

export interface DeleteResultLabelParams {
  labelId: string
}
